// Central language configuration for the trilingual site.
// English is the default and lives at the root (no URL prefix, Shopify model).
// German and Greek live under /de and /el.

export const LANGS = ["en", "de", "el"] as const;
export type Lang = (typeof LANGS)[number];

export const DEFAULT_LANG: Lang = "en";

/** Languages that carry a URL prefix. English (default) has none. */
export const PREFIXED_LANGS: Lang[] = ["de", "el"];

export const LANG_LABELS: Record<Lang, string> = {
  en: "English",
  de: "Deutsch",
  el: "Ελληνικά",
};

export const LANG_SHORT: Record<Lang, string> = {
  en: "EN",
  de: "DE",
  el: "EL",
};

/** Flag emoji per language for the language switcher. */
export const LANG_FLAG: Record<Lang, string> = {
  en: "🇬🇧",
  de: "🇩🇪",
  el: "🇬🇷",
};

/** ISO 3166-1 alpha-2 country codes used to render reliable SVG flags. */
export const LANG_FLAG_CODE: Record<Lang, string> = {
  en: "gb",
  de: "de",
  el: "gr",
};

/** BCP-47 / og:locale codes used in <html lang> and hreflang/OpenGraph. */
export const LANG_HREFLANG: Record<Lang, string> = {
  en: "en",
  de: "de",
  el: "el",
};

export const LANG_OG_LOCALE: Record<Lang, string> = {
  en: "en_US",
  de: "de_DE",
  el: "el_GR",
};

export function isLang(value: string | undefined): value is Lang {
  return !!value && (LANGS as readonly string[]).includes(value);
}

/**
 * Splits a pathname into its language and the un-prefixed "logical" path.
 *  "/de/contact" -> { lang: "de", path: "/contact" }
 *  "/el"         -> { lang: "el", path: "/" }
 *  "/contact"    -> { lang: "en", path: "/contact" }
 *  "/"           -> { lang: "en", path: "/" }
 */
export function parsePath(pathname: string): { lang: Lang; path: string } {
  const segments = pathname.split("/").filter(Boolean);
  const first = segments[0];
  if (isLang(first) && first !== DEFAULT_LANG) {
    const rest = "/" + segments.slice(1).join("/");
    return { lang: first, path: untranslatePath(rest === "/" ? "/" : rest.replace(/\/$/, ""), first) };
  }
  const path = "/" + segments.join("/");
  return { lang: DEFAULT_LANG, path: path === "/" ? "/" : path.replace(/\/$/, "") };
}

/**
 * Builds a prefixed URL for a logical path in the given language.
 *  localizePath("/contact", "de") -> "/de/contact"
 *  localizePath("/contact", "en") -> "/contact"
 *  localizePath("/", "el")        -> "/el"
 *  Preserves hashes: localizePath("/#blueprint", "de") -> "/de#blueprint"
 */
export function localizePath(path: string, lang: Lang): string {
  // Separate an optional hash so we can re-attach it after prefixing.
  const hashIndex = path.indexOf("#");
  const hash = hashIndex >= 0 ? path.slice(hashIndex) : "";
  let clean = hashIndex >= 0 ? path.slice(0, hashIndex) : path;
  if (!clean.startsWith("/")) clean = "/" + clean;
  clean = clean === "/" ? "/" : clean.replace(/\/$/, "");

  if (lang === DEFAULT_LANG) {
    return (clean === "/" ? "/" : clean) + hash;
  }
  const translated = translatePath(clean, lang);
  const prefixed = translated === "/" ? `/${lang}` : `/${lang}${translated}`;
  return prefixed + hash;
}

// ---------------------------------------------------------------------------
// Localized URL slugs. Logical paths are English; German gets its own words
// (e.g. /de/kosten instead of /de/pricing). Each language version has exactly
// one URL, linked via hreflang, so there is no duplicate content.
// ---------------------------------------------------------------------------

/** First-segment translations per language (logical -> localized). */
const SEGMENT_SLUGS: Partial<Record<Lang, Record<string, string>>> = {
  de: {
    pricing: "kosten",
    contact: "kontakt",
    imprint: "impressum",
    "privacy-policy": "datenschutz",
    integrations: "integrationen",
    references: "referenzen",
  },
};

/** Landing pages (/pages/:slug) get a top-level localized slug. */
export const LEAD_MAGNET_SLUGS: Partial<Record<Lang, Record<string, string>>> = {
  de: {
    "build-your-own-tool-instead-of-saas": "eigenes-tool-statt-saas",
    "custom-crm": "individuelles-crm",
    "build-an-app-without-headaches": "app-entwickeln-lassen",
    "custom-portal-not-a-builder": "kundenportal-entwickeln",
    "tools-that-dont-exist-yet": "software-die-es-noch-nicht-gibt",
    "combine-your-tools-into-one-suite": "tools-zusammenfuehren",
    "why-not-vibecode-it-yourself": "vibecoding-vs-softwareentwicklung",
    "ai-built-into-your-software": "ki-in-software-integrieren",
    "custom-software-fixed-price": "individualsoftware-festpreis",
    "your-idea-we-build": "deine-idee-wir-entwickeln",
    "custom-software-development": "individualsoftware-entwickeln-lassen",
    "software-development-agency": "softwareentwicklung-agentur",
  },
};

function invert(map: Record<string, string> = {}): Record<string, string> {
  return Object.fromEntries(Object.entries(map).map(([k, v]) => [v, k]));
}

/** Logical path ("/pricing", "/pages/custom-crm") -> localized path without prefix. */
/**
 * Localized blog slugs (DB-driven, German only). Filled at runtime from
 * blog_post_translations.slug and by the build scripts before they emit URLs.
 */
const BLOG_SLUGS: Partial<Record<Lang, Record<string, string>>> = {};
let blogSlugVersion = 0;
const blogSlugListeners = new Set<() => void>();
export function setBlogSlugs(lang: Lang, map: Record<string, string>) {
  BLOG_SLUGS[lang] = { ...map };
  blogSlugVersion++;
  blogSlugListeners.forEach((l) => l());
}
export const subscribeBlogSlugs = (l: () => void) => {
  blogSlugListeners.add(l);
  return () => {
    blogSlugListeners.delete(l);
  };
};
export const getBlogSlugVersion = () => blogSlugVersion;

export function translatePath(path: string, lang: Lang): string {
  const segs = path.split("/").filter(Boolean);
  if (!segs.length) return "/";
  if (segs[0] === "blog" && segs[1] && BLOG_SLUGS[lang]?.[segs[1]]) {
    segs[1] = BLOG_SLUGS[lang]![segs[1]];
  }
  const lm = LEAD_MAGNET_SLUGS[lang];
  if (segs[0] === "pages" && segs[1] && lm?.[segs[1]]) {
    return "/" + [lm[segs[1]], ...segs.slice(2)].join("/");
  }
  const seg = SEGMENT_SLUGS[lang]?.[segs[0]];
  if (seg) segs[0] = seg;
  return "/" + segs.join("/");
}

/** Localized path without prefix -> logical path. Unknown segments pass through. */
export function untranslatePath(path: string, lang: Lang): string {
  const segs = path.split("/").filter(Boolean);
  if (!segs.length) return "/";
  if (segs[0] === "blog" && segs[1]) {
    const en = invert(BLOG_SLUGS[lang])[segs[1]];
    if (en) segs[1] = en;
  }
  const lm = invert(LEAD_MAGNET_SLUGS[lang]);
  if (lm[segs[0]]) return "/" + ["pages", lm[segs[0]], ...segs.slice(1)].join("/");
  const seg = invert(SEGMENT_SLUGS[lang])[segs[0]];
  if (seg) segs[0] = seg;
  return "/" + segs.join("/");
}

/** Prefix a logical path with its language WITHOUT translating slugs (router-internal). */
export function prefixLogicalPath(path: string, lang: Lang): string {
  if (lang === DEFAULT_LANG) return path;
  return path === "/" ? `/${lang}` : `/${lang}${path}`;
}
